import { ReactNode, useCallback, useContext, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import useAsyncEffect from 'use-async-effect';
import classNames from 'classnames';
import { SlipContext } from 'context/slipContext';
import {
  getPopularSlips,
  type PopularSlip,
} from 'services/api/sportBetsService';
import { findPickInOffer } from 'utils/replayTicket';
import {
  formatTranslatedOddLabelParts,
  translateBetMarketName,
} from 'helpers/betI18n';
import { formatDateTime } from 'helpers/dateHelper';
import { getDefaultSportIconSrc } from 'helpers/sportIcon';
import { SportIcon } from 'components/ThemeIcon/SportIcon';
import { TeamCrest } from 'components/TeamCrest/TeamCrest';
import { Bet } from 'types/Bet';
import { MatchOffer } from 'types/MatchOffer';
import { Odd } from 'types/Odd';
import { ModernSheet } from './ModernSheet';
import { openSlipSheet } from 'lib/slipSheet';
import { RailArrows } from './RailArrows';
import { europeanHandicapPick } from 'helpers/marketLines';
import styles from './PopularSlips.module.css';

type Pick = { match: MatchOffer; bet: Bet; odd: Odd };
export type ResolvedSlip = {
  played: number;
  picks: Pick[];
  total: number;
  /** Under the ••• button instead of the times played (a shared code). */
  label?: string;
};

/**
 * The slips' picks as today's offer has them, priced now. With `partial` a
 * pick missing from the offer is left out of its slip; without it the whole
 * slip is.
 */
export function resolveSlips(
  data: { slips: PopularSlip[]; events: MatchOffer[] } | null,
  partial = false
): ResolvedSlip[] {
  const byFI = new Map((data?.events ?? []).map((m) => [m.fi, m]));
  const now = Date.now();
  const out: ResolvedSlip[] = [];
  (data?.slips ?? []).forEach((s) => {
    const picks: Pick[] = [];
    for (const leg of s.legs) {
      const match = byFI.get(leg.fi);
      const found =
        match && match.time * 1000 > now
          ? findPickInOffer(match, { oddCustomId: leg.odd_custom_id })
          : null;
      if (!match || !found) {
        if (partial) continue;
        return;
      }
      picks.push({ match, ...found });
    }
    if (picks.length === 0) return;
    const total = picks.reduce((acc, p) => acc * p.odd.odds, 1);
    out.push({ played: s.played, picks, total });
  });
  return out;
}

/** Picks shown on a card; the rest are counted (all show in the sheet). */
const CARD_PICKS = 2;

function PickRow({ pick, full }: { pick: Pick; full?: boolean }) {
  const { t } = useTranslation();
  const { match, bet, odd } = pick;
  const parts = formatTranslatedOddLabelParts(odd, t);
  const european = europeanHandicapPick(bet.name, odd);
  const label = european
    ? european.pick
    : [parts.name, parts.handicap, parts.header].filter(Boolean).join(' ');
  return (
    <div className={styles.pick}>
      {full && (
        <div className={styles.pickTime}>
          <span>
            {formatDateTime(new Date(match.time * 1000), {
              removeSeconds: true,
              removeYear: true,
            })}
          </span>
          <SportIcon
            sportId={match.league.sport}
            className={styles.sportIcon}
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = getDefaultSportIconSrc();
            }}
          />
        </div>
      )}
      <div className={styles.team}>
        <TeamCrest imageId={match.home?.image_id} className={styles.crest} />
        <span>{match.home?.name}</span>
      </div>
      <div className={styles.team}>
        <TeamCrest imageId={match.away?.image_id} className={styles.crest} />
        <span>{match.away?.name}</span>
      </div>
      <div className={styles.market}>
        <span className={styles.marketName}>
          {translateBetMarketName(bet, t)}
          {european && ` ${european.line}`}
        </span>
        <span className={styles.pickName}>{label}</span>
        <strong>{odd.odds.toFixed(2)}</strong>
      </div>
    </div>
  );
}

function SlipFooter({
  slip,
  onMore,
}: {
  slip: ResolvedSlip;
  onMore?: () => void;
}) {
  const { t } = useTranslation();
  const { addPrematchSlipEntries, entryExist } = useContext(SlipContext);
  const onSlip = slip.picks.every((p) => entryExist(p.match, p.bet, p.odd));

  return (
    <div className={styles.footer}>
      <button
        type="button"
        className={styles.more}
        onClick={onMore}
        disabled={!onMore}
        aria-label={t('modern_slip_title')}
      >
        <span aria-hidden>•••</span>
        <small>
          {slip.label ??
            (slip.played > 0 ? `${slip.played}x` : t('modern_slip_suggested'))}
        </small>
      </button>
      <button
        type="button"
        className={classNames(styles.total, onSlip && styles.totalOn)}
        title={t('modern_slip_add')}
        onClick={() =>
          onSlip ? openSlipSheet() : addPrematchSlipEntries(slip.picks)
        }
      >
        {slip.total.toFixed(2)}
      </button>
    </div>
  );
}

/** A whole slip in a sheet: every pick with its kick-off, and the button
 *  that puts them on the bet slip. */
export function SlipSheet({
  slip,
  title,
  note,
  close,
}: {
  slip: ResolvedSlip;
  title: ReactNode;
  note?: ReactNode;
  close: () => void;
}) {
  return (
    <ModernSheet title={title} close={close}>
      {note && <p className={styles.note}>{note}</p>}
      <div className={styles.sheetCard}>
        <div className={styles.sheetPicks}>
          {slip.picks.map((p) => (
            <PickRow key={`${p.match.fi}-${p.odd.custom_id}`} pick={p} full />
          ))}
        </div>
        <SlipFooter slip={slip} />
      </div>
    </ModernSheet>
  );
}

/** Modern home: "Most played bet slips" - open tickets' picks that have not
 *  started yet, topped up with suggestions; one tap puts a slip's picks on the
 *  bet slip at today's prices. */
export const PopularSlips = ({
  bump,
  desktop = false,
}: {
  bump: number;
  desktop?: boolean;
}) => {
  const { t } = useTranslation();
  const [slips, setSlips] = useState<ResolvedSlip[] | null>(null);
  const [open, setOpen] = useState<ResolvedSlip | null>(null);
  const close = useCallback(() => setOpen(null), []);
  const rail = useRef<HTMLDivElement>(null);

  useAsyncEffect(
    async (isMounted) => {
      const data = await getPopularSlips();
      if (!isMounted()) return;
      setSlips(resolveSlips(data));
    },
    [bump]
  );

  if (!slips || slips.length === 0) return null;

  return (
    <>
      <div
        className={
          desktop ? `${styles.head} ${styles.headDesktop}` : styles.head
        }
      >
        <h2>{t('modern_most_played_slips')}</h2>
        {desktop && <RailArrows rail={rail} />}
      </div>
      <div
        className={
          desktop ? `${styles.rail} ${styles.railDesktop}` : styles.rail
        }
        ref={rail}
      >
        {slips.map((s, i) => (
          <div key={i} className={styles.card}>
            <button
              type="button"
              className={styles.cardBody}
              onClick={() => setOpen(s)}
            >
              {s.picks.slice(0, CARD_PICKS).map((p) => (
                <PickRow key={`${p.match.fi}-${p.odd.custom_id}`} pick={p} />
              ))}
              {s.picks.length > CARD_PICKS && (
                <span className={styles.morePicks}>
                  {t('modern_slip_more_picks', {
                    count: s.picks.length - CARD_PICKS,
                  })}
                </span>
              )}
            </button>
            <SlipFooter slip={s} onMore={() => setOpen(s)} />
          </div>
        ))}
      </div>
      {open && (
        <SlipSheet
          slip={open}
          title={t('modern_most_played_slips')}
          close={close}
        />
      )}
    </>
  );
};
